import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileBadge2, ScrollText, UserCheck } from 'lucide-react'
import {
  getVolunteerAgreement, listVolunteerApplications, listVolunteerCertificates, reviewVolunteerApplication,
} from '../../../api/admin'
import type { VolunteerApplication } from '../../../api/schemas'
import { QueryBoundary } from '../../../components/common/QueryBoundary'
import { StatusBadge } from '../../../components/common/Status'
import { TimeAgo } from '../../../components/common/TimeAgo'
import { Skeleton } from '../../../components/ui/skeleton'
import { Button } from '../../../components/ui/button'
import { Textarea } from '../../../components/ui/textarea'
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog'
import { formatAbsTs, formatNum } from '../../../lib/format'
import { hasPermission } from '../../../lib/authz'
import { qk } from '../../../lib/query-keys'
import { useTokenClaims } from '../../../stores/auth'
import { toast } from '../../../stores/toast'

const PAGE_SIZE = 20

const CERTIFICATE_STATUS_LABEL: Record<string, string> = {
  active: '有效',
  issued: '已发放',
  revoked: '已撤销',
  expired: '已过期',
}

type SectionTab = 'applications' | 'certificates' | 'agreement'

/** 志愿者中心：申请审核 / 证书发放记录（只读，需 tasks.read）/ 志愿者协议查看器 */
export function VolunteerSection() {
  const claims = useTokenClaims()
  const canApplications = hasPermission(claims, 'volunteer.applications.read')
  const canCertificates = hasPermission(claims, 'tasks.read')

  const [tab, setTab] = useState<SectionTab>(canApplications ? 'applications' : canCertificates ? 'certificates' : 'agreement')

  return (
    <div className="panel-glass overflow-hidden rounded-xl">
      <div className="border-b border-border px-4 py-3">
        <Tabs value={tab} onValueChange={(v) => setTab(v as SectionTab)}>
          <TabsList>
            {canApplications && <TabsTrigger value="applications">申请审核</TabsTrigger>}
            {canCertificates && <TabsTrigger value="certificates">证书发放记录</TabsTrigger>}
            <TabsTrigger value="agreement">志愿者协议</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {tab === 'applications' && canApplications && <ApplicationsPanel />}
      {tab === 'certificates' && canCertificates && <CertificatesPanel />}
      {tab === 'agreement' && <AgreementPanel />}
    </div>
  )
}

// ---------- 申请审核 ----------

function ApplicationsPanel() {
  const [status, setStatus] = useState('pending')
  const [page, setPage] = useState(1)
  const [reviewing, setReviewing] = useState<VolunteerApplication | null>(null)

  const query = useQuery({
    queryKey: ['v2', 'volunteers', { status, page }],
    queryFn: () => listVolunteerApplications({ status: status === 'all' ? undefined : status, page, pageSize: PAGE_SIZE }),
    retry: 1,
  })

  const data = query.data
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1

  return (
    <>
      <div className="border-b border-border px-4 py-3">
        <Tabs
          value={status}
          onValueChange={(v) => {
            setStatus(v)
            setPage(1)
          }}
        >
          <TabsList>
            <TabsTrigger value="pending">待审核</TabsTrigger>
            <TabsTrigger value="approved">已批准</TabsTrigger>
            <TabsTrigger value="rejected">已驳回</TabsTrigger>
            <TabsTrigger value="all">全部</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <QueryBoundary
        query={query}
        skeleton={<div className="space-y-2 p-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>}
        isEmpty={(d) => d.items.length === 0}
        empty={{ title: '暂无申请', description: '志愿者提交申请后在此审核', icon: <UserCheck size={24} /> }}
      >
        {(d) => (
          <>
            <table className="w-full text-left">
              <thead className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 font-medium">申请人</th>
                  <th className="px-4 py-2.5 font-medium">区域</th>
                  <th className="px-4 py-2.5 font-medium">动机</th>
                  <th className="px-4 py-2.5 font-medium">技能</th>
                  <th className="px-4 py-2.5 font-medium">状态</th>
                  <th className="px-4 py-2.5 font-medium">提交时间</th>
                  <th className="px-4 py-2.5 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {d.items.map((app) => (
                  <tr key={app.applicationId} className="text-xs transition hover:bg-foreground/[.03]">
                    <td className="px-4 py-2.5 font-mono text-[11px] text-foreground/90">{app.userId}</td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{app.regionCode ?? '—'}</td>
                    <td className="max-w-[220px] px-4 py-2.5">
                      <span className="line-clamp-2 text-muted-foreground">{app.motivation}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {app.skillCodes.length === 0 ? (
                          <span className="text-muted-foreground/40">—</span>
                        ) : (
                          app.skillCodes.map((s) => (
                            <span key={s} className="rounded bg-foreground/[.07] px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                              {s}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5"><StatusBadge status={app.status} /></td>
                    <td className="px-4 py-2.5"><TimeAgo ts={app.createdAt} className="text-muted-foreground" /></td>
                    <td className="px-4 py-2.5">
                      {app.status === 'pending' && (
                        <Button size="sm" variant="secondary" onClick={() => setReviewing(app)}>
                          审核
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
              <span>共 {d.total} 条 · 第 {page}/{totalPages} 页</span>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>上一页</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>下一页</Button>
              </div>
            </div>
          </>
        )}
      </QueryBoundary>

      <ReviewDialog application={reviewing} onClose={() => setReviewing(null)} />
    </>
  )
}

function ReviewDialog({ application, onClose }: { application: VolunteerApplication | null; onClose: () => void }) {
  const qc = useQueryClient()
  const [note, setNote] = useState('')

  const mutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') =>
      reviewVolunteerApplication(application!.applicationId, {
        status: decision,
        note: note.trim() || undefined,
      }),
    onSuccess: (_data, decision) => {
      toast.success(decision === 'approved' ? '已批准申请' : '已驳回申请')
      void qc.invalidateQueries({ queryKey: ['v2', 'volunteers'] })
      void qc.invalidateQueries({ queryKey: ['v2', 'audit'] })
      onClose()
    },
    onError: (err: Error) => toast.error('审核失败', err.message),
  })

  return (
    <Dialog open={Boolean(application)} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>审核志愿者申请</DialogTitle>
          <DialogDescription>
            {application ? `申请人：${application.userId} · ${application.regionCode ?? '全局'}` : ''}
          </DialogDescription>
        </DialogHeader>
        {application && (
          <div className="space-y-3">
            <div className="rounded-lg bg-black/20 p-3 text-[11px] leading-4 text-muted-foreground">
              {application.motivation}
            </div>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="审核备注（可选）"
              className="min-h-[64px]"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate('rejected')}
              >
                驳回
              </Button>
              <Button
                size="sm"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate('approved')}
              >
                批准
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ---------- 证书发放记录（只读） ----------

function CertificatesPanel() {
  const query = useQuery({
    queryKey: qk.admin.certificates,
    queryFn: listVolunteerCertificates,
    staleTime: 30_000,
    retry: 1,
  })

  return (
    <QueryBoundary
      query={query}
      skeleton={<div className="space-y-2 p-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>}
      isEmpty={(d) => d.items.length === 0}
      empty={{
        title: '暂无证书发放记录',
        description: '志愿者完成任务并累计服务时长后，发放记录在此只读展示',
        icon: <FileBadge2 size={24} />,
      }}
    >
      {(d) => {
        const sumMinutes = d.items.reduce((acc, c) => acc + c.serviceMinutes, 0)
        return (
          <>
            <div className="grid grid-cols-2 gap-3 border-b border-border p-4 md:grid-cols-4">
              <StatCell label="发放总数" value={formatNum(d.totalCount ?? d.items.length)} />
              <StatCell label="累计服务时长" value={`${formatNum(d.totalMinutes ?? sumMinutes)} 分钟`} />
            </div>
            <table className="w-full text-left">
              <thead className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 font-medium">证书编号</th>
                  <th className="px-4 py-2.5 font-medium">志愿者</th>
                  <th className="px-4 py-2.5 font-medium">关联任务</th>
                  <th className="px-4 py-2.5 font-medium">服务时长</th>
                  <th className="px-4 py-2.5 font-medium">发放时间</th>
                  <th className="px-4 py-2.5 font-medium">状态</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {d.items.map((cert) => (
                  <tr key={cert.certificateId} className="text-xs transition hover:bg-foreground/[.03]">
                    <td className="px-4 py-2.5 font-mono text-[11px] text-foreground/90">{cert.certificateId}</td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{cert.userId}</td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{cert.taskId || '—'}</td>
                    <td className="px-4 py-2.5 font-mono tabular-nums text-muted-foreground">{formatNum(cert.serviceMinutes)} 分钟</td>
                    <td className="px-4 py-2.5"><TimeAgo ts={cert.issuedAt} className="text-muted-foreground" /></td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={cert.status} labels={CERTIFICATE_STATUS_LABEL} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )
      }}
    </QueryBoundary>
  )
}

function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-black/20 p-3">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-sm font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  )
}

// ---------- 志愿者协议查看器 ----------

function AgreementPanel() {
  const [open, setOpen] = useState(false)
  const query = useQuery({
    queryKey: qk.admin.agreement,
    queryFn: getVolunteerAgreement,
    staleTime: 5 * 60_000,
    retry: 1,
  })

  return (
    <QueryBoundary
      query={query}
      skeleton={<div className="space-y-2 p-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>}
      empty={{ title: '协议暂未发布', description: '后端尚未配置志愿者服务协议内容' }}
    >
      {(a) => (
        <div className="p-5">
          <div className="flex flex-wrap items-center gap-2">
            <ScrollText size={15} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">{a.title || '志愿者服务协议'}</span>
            {a.version && (
              <span className="rounded bg-foreground/[.07] px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                v{a.version}
              </span>
            )}
            <span className="ml-auto text-[11px] text-muted-foreground">
              更新于 <TimeAgo ts={a.updatedAt} />
            </span>
          </div>
          <p className="mt-2 text-[11px] leading-5 text-muted-foreground">
            当前生效的志愿者服务协议版本。志愿者在移动端报名与接单时需同意本协议，管理员可在此查看全文。
          </p>
          <Button size="sm" variant="secondary" className="mt-3" onClick={() => setOpen(true)}>
            查看协议全文
          </Button>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{a.title || '志愿者服务协议'}</DialogTitle>
                <DialogDescription>
                  版本 {a.version ? `v${a.version}` : '—'} · 更新于 {a.updatedAt ? formatAbsTs(a.updatedAt) : '—'}
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap rounded-lg bg-black/20 p-4 text-xs leading-6 text-foreground/85">
                {a.content || '（协议内容为空）'}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </QueryBoundary>
  )
}
